import React, { useState } from "react";
import styled from "styled-components";

const FAQSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(
    135deg,
    var(--background-light) 0%,
    var(--background-medium) 100%
  );
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;

  .icon {
    width: 8px;
    height: 8px;
    background: var(--secondary-color);
    border-radius: 50%;
  }

  span {
    color: var(--secondary-color);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.9rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AccordionItem = styled.div`
  background: white;
  border-radius: 15px;
  margin-bottom: 15px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const AccordionHeader = styled.button`
  width: 100%;
  padding: 25px 30px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(245, 115, 58, 0.05);
  }

  .icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--secondary-color);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    transition: transform 0.3s ease;

    ${(props) =>
      props.isOpen &&
      `
      transform: rotate(45deg);
    `}
  }
`;

const AccordionContent = styled.div`
  padding: ${(props) => (props.isOpen ? "0 30px 25px 30px" : "0 30px")};
  max-height: ${(props) => (props.isOpen ? "200px" : "0")};
  overflow: hidden;
  transition: all 0.3s ease;

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin: 0;

    strong {
      color: var(--text-dark);
    }
  }
`;

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqs = [
    {
      question: "What sizes of containers do you offer?",
      answer:
        "We stock 10ft, 20ft, 40ft, and High Cube containers. Specialized sizes may also be available on request.",
    },
    {
      question: "How do I know if a used container is right for me?",
      answer:
        "Our used containers are carefully inspected and graded. We'll help you choose based on your specific needs, budget, and intended use. All our used containers are wind and watertight certified.",
    },
    {
      question: "Do you deliver nationwide?",
      answer:
        "Yes! With our head office in Kentucky and regional partners in New Jersey and Utah, we can deliver containers to all 50 US states quickly and affordably.",
    },
    {
      question: "What custom modifications can you do?",
      answer:
        "We specialize in side doors, roll-up doors, windows, vents, insulation, paint, branding, and complete conversions into offices, workshops, or living spaces. Contact us to discuss your specific needs.",
    },
    {
      question: "How long does delivery take?",
      answer:
        "Delivery times vary by location and container availability, but we typically deliver within 1-2 weeks of order confirmation. Rush delivery may be available for an additional fee.",
    },
  ];

  return (
    <FAQSection>
      <Container>
        <SectionHeader>
          <SectionTag>
            <div className="icon"></div>
            <span>Our FAQs</span>
          </SectionTag>

          <SectionTitle>Frequently Asked Questions</SectionTitle>
        </SectionHeader>

        <div>
          {faqs.map((faq, index) => (
            <AccordionItem key={index}>
              <AccordionHeader
                onClick={() => toggleItem(index)}
                isOpen={openItems.has(index)}
              >
                {faq.question}
                <div className="icon">{openItems.has(index) ? "−" : "+"}</div>
              </AccordionHeader>

              <AccordionContent isOpen={openItems.has(index)}>
                <p>{faq.answer}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </div>
      </Container>
    </FAQSection>
  );
};

export default FAQ;
